import {
  InternalChannelStats,
  InternalOpsChannel,
  InternalOpsChannelMemberUser,
  useInternalChannelStats,
} from "@/lib/api/internal-ops-messaging";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import {
  CalendarIcon,
  ChevronDoubleRightIcon,
  CircleStackIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { ComponentType, SVGProps, useMemo, useState } from "react";

interface ChatDetailsPanelProps {
  internalChannel: InternalOpsChannel | null;
}

export function ChatDetailsPanel({ internalChannel }: ChatDetailsPanelProps) {
  const sb = useSendbirdStateContext();
  const currentUserId = sb.config.userId;
  const { currentGroupChannel } = useChannelContext();

  const [collapsed, setCollapsed] = useState(false);

  const otherEmbeddedMember = useMemo(() => {
    if (!internalChannel?.members) return null;
    return (
      internalChannel.members.find(
        (m) => m.user_id !== currentUserId && !!m.user,
      ) ?? null
    );
  }, [internalChannel, currentUserId]);

  const isCohort = internalChannel?.kind === "cohort";
  const isGroup = internalChannel?.kind === "group";
  const showStatsPanel = isCohort || isGroup;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="flex w-12 cursor-pointer items-center justify-center border-l border-zinc-200 bg-white"
        aria-label="Expand details"
      >
        <ChevronDoubleRightIcon className="size-5 rotate-180 text-zinc-400" />
      </button>
    );
  }

  return (
    <aside className="flex w-[360px] flex-col border-l border-zinc-200 bg-[#f9fafb]">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-4">
        <h3 className="text-sm font-semibold text-black">Details</h3>
        <button
          onClick={() => setCollapsed(true)}
          className="text-zinc-400 hover:text-zinc-600"
          aria-label="Collapse details"
        >
          <ChevronDoubleRightIcon className="size-5" />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">
        {showStatsPanel && internalChannel ? (
          <ChannelStatsDetails
            channelId={internalChannel.id}
            kind={isCohort ? "cohort" : "group"}
            fallbackDisplayName={
              internalChannel.channel_display_name ?? currentGroupChannel?.name
            }
          />
        ) : otherEmbeddedMember?.user ? (
          <DirectDetails user={otherEmbeddedMember.user} />
        ) : (
          <div className="text-sm text-zinc-500">No member selected</div>
        )}
      </div>
    </aside>
  );
}

function ChannelStatsDetails({
  channelId,
  kind,
  fallbackDisplayName,
}: {
  channelId: string;
  kind: "cohort" | "group";
  fallbackDisplayName?: string | null;
}) {
  const { data: stats, isLoading } = useInternalChannelStats(channelId);

  const subtitle = useMemo(() => buildStatsSubtitle(stats ?? null), [stats]);
  const avgTenure = useMemo(
    () => formatTenureMonths(stats?.avgTenureMonths ?? null),
    [stats?.avgTenureMonths],
  );
  const groupsValue = useMemo(() => {
    if (!stats || kind !== "cohort") return null;
    return stats.groups.length === 0 ? "All" : stats.groups.join(", ");
  }, [stats, kind]);

  return (
    <>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-bold leading-7 text-[#020817]">
          {stats?.displayName ||
            fallbackDisplayName ||
            (kind === "cohort" ? "Cohort" : "Group")}
        </h2>
        {subtitle && (
          <p className="text-base leading-6 text-[#6b7280]">{subtitle}</p>
        )}
      </div>

      <div className="border-t border-[#e2e2e2] pt-4">
        <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_2px_4px_0_rgba(0,0,0,0.05)]">
          {isLoading && !stats ? (
            <div className="space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-zinc-100" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-zinc-100" />
              <div className="h-4 w-1/3 animate-pulse rounded bg-zinc-100" />
            </div>
          ) : (
            <>
              {avgTenure && (
                <InfoRow
                  icon={CalendarIcon}
                  title="Average time with Somethings"
                  value={avgTenure}
                />
              )}
              {stats && stats.stateCount > 0 && (
                <InfoRow
                  icon={MapPinIcon}
                  title="Locations"
                  value={`${stats.stateCount} ${
                    stats.stateCount === 1 ? "state" : "states"
                  }`}
                />
              )}
              {groupsValue && (
                <InfoRow
                  icon={CircleStackIcon}
                  title="Groups"
                  value={groupsValue}
                />
              )}
              {stats && (
                <InfoRow
                  icon={UsersIcon}
                  title="Members"
                  value={`${stats.memberCount} ${
                    stats.memberCount === 1 ? "member" : "members"
                  }`}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function buildStatsSubtitle(stats: InternalChannelStats | null): string | null {
  if (!stats) return null;
  const parts: string[] = [];
  if (stats.memberCount > 0) {
    parts.push(
      `${stats.memberCount} ${stats.memberCount === 1 ? "member" : "members"}`,
    );
  }
  if (stats.ageRange) {
    const { min, max } = stats.ageRange;
    parts.push(min === max ? `${min} years old` : `${min} - ${max} years old`);
  }
  return parts.length > 0 ? parts.join(" • ") : null;
}

function formatTenureMonths(months: number | null): string | null {
  if (months == null) return null;
  if (months < 1) return "Less than a month";
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  if (remMonths > 0)
    parts.push(`${remMonths} ${remMonths === 1 ? "month" : "months"}`);
  return parts.join(", ");
}

function DirectDetails({ user }: { user: InternalOpsChannelMemberUser }) {
  const subtitleLabel = useMemo(() => formatRoleLabel(user.role), [user.role]);

  const tenure = useMemo(() => {
    if (!user.created_at) return null;
    const created = DateTime.fromISO(user.created_at);
    if (!created.isValid) return null;
    return formatTenureMonths(
      Math.floor(DateTime.now().diff(created, "months").months),
    );
  }, [user.created_at]);

  const subtitleParts = [subtitleLabel, user.pronouns].filter(
    (part): part is string => !!part && part.length > 0,
  );

  return (
    <>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-bold leading-7 text-[#020817]">
          {user.name}
        </h2>
        {subtitleParts.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-3 text-base leading-6 text-[#6b7280]">
            {subtitleParts.map((part, idx) => (
              <span
                key={`${part}-${idx}`}
                className="inline-flex items-center gap-3"
              >
                {idx > 0 && (
                  <span aria-hidden className="text-[#6b7280]">
                    •
                  </span>
                )}
                <span>{part}</span>
              </span>
            ))}
          </p>
        )}
      </div>

      <div className="border-t border-[#e2e2e2] pt-4">
        <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_2px_4px_0_rgba(0,0,0,0.05)]">
          {tenure && (
            <InfoRow
              icon={CalendarIcon}
              title="Time with Somethings"
              value={tenure}
            />
          )}
          {user.email && (
            <InfoRow icon={EnvelopeIcon} title="Email" value={user.email} />
          )}
          {user.phone && (
            <InfoRow icon={PhoneIcon} title="Phone" value={user.phone} />
          )}
          {user.location && (
            <InfoRow icon={MapPinIcon} title="Location" value={user.location} />
          )}
        </div>
      </div>
    </>
  );
}

function InfoRow({
  icon: Icon,
  title,
  value,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="size-5 shrink-0 text-black" />
      <div className="flex min-w-0 flex-1 flex-col text-xs">
        <span className="font-bold text-black">{title}</span>
        <span className="truncate text-[#5c5c5c]">{value}</span>
      </div>
    </div>
  );
}
